"use client";

import * as React from "react";
import * as SliderPrimitive from "@radix-ui/react-slider";

import { cn } from "@punchup/utils/cn";

interface SliderProps extends React.ComponentPropsWithoutRef<
  typeof SliderPrimitive.Root
> {
  showMarkers?: boolean;
  markerStepSize?: number;
  containerClassName?: string;
  trackClassName?: string;
  thumbClassName?: string;
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      showMarkers,
      markerStepSize,
      containerClassName,
      trackClassName,
      thumbClassName,
      min = 0,
      max = 100,
      step = 1,
      ...props
    },
    ref
  ) => {
    const thumbWidth = 20; // h-5 w-5 = 20px
    const thumbRadius = thumbWidth / 2; // 10px

    const markers = React.useMemo(() => {
      if (!showMarkers) return [];
      const markerData = [];
      const stepSize = markerStepSize || step;
      for (let value = min; value <= max; value += stepSize) {
        const percentage = ((value - min) / (max - min)) * 100;
        const pixelOffset = thumbRadius - (percentage * thumbWidth) / 100;
        const position = `calc(${percentage}% + ${pixelOffset}px)`;
        markerData.push({ value, position });
      }
      return markerData;
    }, [showMarkers, min, max, step, markerStepSize, thumbRadius, thumbWidth]);

    return (
      <div className={cn("relative w-full mb-8", containerClassName)}>
        {showMarkers && (
          <div
            className="absolute w-full top-0 flex items-center"
            style={{ height: "16px" }}
          >
            {markers.map((marker) => {
              return (
                <div
                  key={marker.value}
                  className="absolute flex flex-col items-center"
                  style={{
                    left: marker.position,
                    transform: "translateX(-50%)",
                    top: "100%",
                  }}
                >
                  <div className="w-px h-2 bg-primary-blue/20 mb-2" />
                  <span className="text-sm text-primary-blue">
                    {marker.value}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <SliderPrimitive.Root
          ref={ref}
          className={cn(
            "relative flex w-full touch-none select-none items-center",
            className
          )}
          min={min}
          max={max}
          step={step}
          {...props}
        >
          <SliderPrimitive.Track
            className={cn(
              "relative h-2 w-full grow overflow-hidden rounded-full bg-primary/20 hover:cursor-pointer",
              trackClassName
            )}
          >
            <SliderPrimitive.Range className="absolute h-full bg-primary" />
          </SliderPrimitive.Track>
          <SliderPrimitive.Thumb
            className={cn(
              "relative z-10 block h-5 w-5 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer data-[disabled]:cursor-default",
              thumbClassName
            )}
          />
        </SliderPrimitive.Root>
      </div>
    );
  }
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
